/**
 * API client functions for immunization catalog operations
 */

import { API_ENDPOINTS } from '../config/api';
import { ImmunizationCatalogItem, ImmunizationCatalogItemCreate, ImmunizationCatalogItemUpdate } from '../types/immunization-catalog';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
}

/**
 * Get all immunization catalog items for a practice
 */
export async function getImmunizationCatalog(
  practiceId: string,
  search?: string,
  activeOnly: boolean = true
): Promise<ImmunizationCatalogItem[]> {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  params.append('active_only', activeOnly.toString());

  const url = `${API_ENDPOINTS.IMMUNIZATION_CATALOG.LIST(practiceId)}?${params.toString()}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch catalog items' }));
    throw new Error(error.detail || 'Failed to fetch catalog items');
  }

  return response.json();
}

/**
 * Create a new immunization catalog item
 */
export async function createCatalogItem(
  practiceId: string,
  data: ImmunizationCatalogItemCreate
): Promise<ImmunizationCatalogItem> {
  const response = await fetch(API_ENDPOINTS.IMMUNIZATION_CATALOG.CREATE(practiceId), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create catalog item' }));
    throw new Error(error.detail || 'Failed to create catalog item');
  }

  return response.json();
}

/**
 * Update an immunization catalog item
 */
export async function updateCatalogItem(
  practiceId: string,
  itemId: string,
  data: ImmunizationCatalogItemUpdate
): Promise<ImmunizationCatalogItem> {
  const response = await fetch(API_ENDPOINTS.IMMUNIZATION_CATALOG.UPDATE(practiceId, itemId), {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update catalog item' }));
    throw new Error(error.detail || 'Failed to update catalog item');
  }

  return response.json();
}

/**
 * Get a single immunization catalog item by ID
 */
export async function getImmunizationCatalogItem(
  practiceId: string,
  itemId: string
): Promise<ImmunizationCatalogItem> {
  const response = await fetch(`${API_ENDPOINTS.IMMUNIZATION_CATALOG.LIST(practiceId)}/${itemId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch immunization catalog item' }));
    throw new Error(error.detail || 'Failed to fetch immunization catalog item');
  }

  return response.json();
}

/**
 * Delete (soft delete) an immunization catalog item
 */
export async function deleteCatalogItem(
  practiceId: string,
  itemId: string
): Promise<void> {
  const response = await fetch(API_ENDPOINTS.IMMUNIZATION_CATALOG.DELETE(practiceId, itemId), {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete catalog item' }));
    throw new Error(error.detail || 'Failed to delete catalog item');
  }
}

/**
 * Permanently delete an immunization catalog item (hard delete)
 * Only works on custom items with no references
 */
export async function hardDeleteCatalogItem(
  practiceId: string,
  itemId: string
): Promise<void> {
  const response = await fetch(API_ENDPOINTS.IMMUNIZATION_CATALOG.HARD_DELETE(practiceId, itemId), {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete catalog item' }));
    throw new Error(error.detail || 'Failed to delete catalog item');
  }
}



